import { useGoogleMap } from '@react-google-maps/api'
import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import useIntl from '@/utils/intl'
import { descriptionToText, kmlToGeoJson, type KmlStyle } from '@/utils/kml'

interface Props {
  url: string
  zIndex?: number
  onError?: (error: unknown) => void
}

// Drop-in replacement for <KmlLayer>, which Google removed from the Maps
// JavaScript API in 3.66. Fetches the KML and draws it on a google.maps.Data
// layer, keeping the KML's own colours and icons and the click info window.
// Only Placemarks are drawn: the coverage KMLs have no ScreenOverlay,
// GroundOverlay or NetworkLink, so KmlLayer's screenOverlays option had
// nothing to show.
const KmlDataLayer: FunctionComponent<Props> = ({ url, zIndex = 1, onError }) => {
  const map = useGoogleMap()
  const { translate } = useIntl()
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    if (map === null || url === '') return
    setFailed(false)
    const controller = new AbortController()
    const data = new google.maps.Data({ map })
    const infoWindow = new google.maps.InfoWindow()

    data.setStyle((feature) => {
      const style = (feature.getProperty('style') ?? {}) as KmlStyle
      const iconSize = 32 * (style.iconScale ?? 1)
      return {
        zIndex,
        fillColor: style.fillColor,
        fillOpacity: style.fillOpacity,
        strokeColor: style.strokeColor ?? style.fillColor,
        strokeOpacity: style.strokeOpacity,
        strokeWeight: style.strokeWeight,
        icon: style.iconUrl !== undefined
          ? {
              url: style.iconUrl,
              scaledSize: new google.maps.Size(iconSize, iconSize),
              anchor: new google.maps.Point(iconSize / 2, iconSize)
            }
          : undefined
      }
    })

    const clickListener = data.addListener('click', (event: google.maps.Data.MouseEvent) => {
      const name = String(event.feature.getProperty('name') ?? '')
      const description = descriptionToText(String(event.feature.getProperty('description') ?? ''))
      if (name === '' && description === '') return

      const content = document.createElement('div')
      content.style.color = '#000'
      content.style.maxWidth = '260px'
      if (name !== '') {
        const title = document.createElement('strong')
        title.textContent = name
        content.appendChild(title)
      }
      if (description !== '' && description !== name) {
        const body = document.createElement('p')
        body.style.whiteSpace = 'pre-line'
        body.style.margin = '4px 0 0'
        body.textContent = description
        content.appendChild(body)
      }
      infoWindow.setContent(content)
      infoWindow.setPosition(event.latLng)
      infoWindow.open({ map })
    })

    fetch(url, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return await response.text()
      })
      .then((kmlText) => { data.addGeoJson(kmlToGeoJson(kmlText)) })
      .catch((error) => {
        if (controller.signal.aborted) return
        console.error('Failed to load KML file:', error)
        setFailed(true)
        onErrorRef.current?.(error)
      })

    return () => {
      controller.abort()
      google.maps.event.removeListener(clickListener)
      infoWindow.close()
      data.setMap(null)
    }
  }, [map, url, zIndex, attempt])

  if (!failed || map === null) return null

  return createPortal(
    <div
      role='alert'
      className='absolute left-1/2 top-4 -translate-x-1/2 z-10 flex items-center gap-3 rounded-lg bg-white px-4 py-2 shadow-md text-sm text-black'
    >
      <span>{translate('map.layerLoadError')}</span>
      <button
        type='button'
        className='font-semibold text-[#4436fa] cursor-pointer'
        onClick={() => { setAttempt((n) => n + 1) }}
      >
        {translate('map.retry')}
      </button>
    </div>,
    map.getDiv()
  )
}

export default KmlDataLayer
